"use client"
import { useState, useRef } from "react"
import { isFilled, KeyTextField, ImageField } from "@prismicio/client"
import Grid from "@aura-design/system/grid"
import { PrismicNextImage } from "@prismicio/next"
import { ChevronRightIcon, ChevronLeftIcon } from "@radix-ui/react-icons"
import Button from "@aura-design/system/button"

type TabIconProps = {
  label: KeyTextField
  icon: ImageField
}

export const Tabs = ({
  children,
  data,
  className,
  classNameWrapper,
  classNameNav,
  classNameContainer,
}: TabsProps) => {
  const [activeTab, setActiveTab] = useState<number>(0)

  const scrollStep = 200
  const contentRef = useRef(null)
  const [scrollLeft, setScrollLeft] = useState(0)

  const handleScrollLeft = () => {
    const newScrollLeft = Math.max(scrollLeft - scrollStep, 0) // Ensure it doesn't go negative
    contentRef.current.scrollLeft = newScrollLeft
    setScrollLeft(newScrollLeft)
  }

  const handleScrollRight = () => {
    const newScrollLeft = Math.min(
      scrollLeft + scrollStep,
      contentRef.current.scrollWidth - contentRef.current.clientWidth,
    ) // Prevent exceeding scrollable content
    contentRef.current.scrollLeft = newScrollLeft
    setScrollLeft(newScrollLeft)
  }

  // Ensure children is an array
  const childArray = Array.isArray(children) ? children : [children]

  // Creating an array of CSS classes to be applied to the tabs element container
  const classNameConnect: string[] = [className!, "nav-list", "start"]

  return (
    <div className={classNameWrapper}>
      <nav className={classNameNav}>
        <div className="hide-medium hide-large">
          <div className={`absolute left-0 top-2.5 z-20 ${scrollLeft === 0 ? "hidden" : "block"}`}>
            <Button mode="link" onClick={handleScrollLeft}>
              <ChevronLeftIcon />
            </Button>
          </div>
          <div className={`absolute right-0 top-2.5 z-20`}>
            <Button mode="link" onClick={handleScrollRight}>
              <ChevronRightIcon />
            </Button>
          </div>
        </div>
        <ul className={classNameConnect.join(" ").trim()} ref={contentRef}>
          {data.map((item, index) => (
            <li key={index}>
              <Button
                mode="link"
                className={index === activeTab ? "active" : ""}
                onClick={() => setActiveTab(index)}
                {...item}
              />
            </li>
          ))}
        </ul>
      </nav>
      <div className={classNameContainer}>{childArray[activeTab]}</div>
    </div>
  )
}

const TabIcon = ({ label, icon }: TabIconProps): JSX.Element => {
  if (!isFilled.keyText(label) || !isFilled.image(icon)) {
    return <></>
  }

  return (
    <Grid col="twelve" className="aura" isFixed>
      <div className="span-4 fixed">
        {isFilled.image(icon) && <PrismicNextImage field={icon} />}
      </div>{" "}
      <div className="span-8 valign">
        {isFilled.keyText(label) && <span> {label} </span>}
      </div>
    </Grid>
  )
}

export default TabIcon
